// Enriches ledger conversations with Foundry GenAI trace detail (tokens, tools,
// queries/sources, finish reason), joined on gen_ai.response.id === responseId
// (exact equality, F2-verified). KQL over AppDependencies (DependencyType=="AI");
// gen_ai.* parsed via todynamic(Properties)[...] — NEVER `has` (term-tokenized;
// misses sub-token ids → false "no match"). Pure parse/merge split from the I/O.
import { LogsQueryClient, LogsQueryResultStatus } from "@azure/monitor-query";
import type { TokenCredential } from "@azure/core-auth";
import type { LedgerConversation, LedgerToolUse } from "./types.js";

const SAFE_ID = /^[A-Za-z0-9_]+$/; // resp ids are resp_<alnum>; guard the in() interpolation

export function buildEnrichKql(respIds: string[]): string {
  const list = respIds.filter((id) => SAFE_ID.test(id)).map((id) => `'${id}'`).join(",");
  if (list === "") return "AppDependencies | take 0";
  return `
AppDependencies
| where DependencyType == "AI"
| extend P = todynamic(Properties)
| extend resp = tostring(P["gen_ai.response.id"]), conv = tostring(P["gen_ai.conversation.id"]),
         op = tostring(P["gen_ai.operation.name"])
| where resp in (${list})
| project resp, conv, op,
          inTok = tolong(P["gen_ai.usage.input_tokens"]),
          outTok = tolong(P["gen_ai.usage.output_tokens"]),
          cachedTok = tolong(P["gen_ai.usage.cached_tokens"]),
          finish = tostring(P["gen_ai.response.finish_reasons"]),
          toolName = tostring(P["gen_ai.tool.name"]),
          toolType = tostring(P["gen_ai.tool.type"]),
          toolArgs = tostring(P["gen_ai.tool.call.arguments"]),
          toolResult = tostring(P["gen_ai.tool.call.result"])
`;
}

export interface RawSpan {
  resp: string; conv: string; op: string;
  inTok: number; outTok: number; cachedTok: number;
  finish: string; toolName: string; toolType: string; toolArgs: string; toolResult: string;
}

export interface RoundEnrichment {
  inputTokens: number; outputTokens: number; cachedTokens: number;
  tools: LedgerToolUse[]; finishReason?: string;
}

// gen_ai.response.finish_reasons is an array per OTel; KQL tostring() yields
// '["stop"]'. Normalize to the first reason, tolerating a plain string too.
function firstFinish(raw: string): string | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  try {
    const v: unknown = JSON.parse(t);
    if (Array.isArray(v)) return v.length ? (typeof v[0] === "string" ? v[0] : String(v[0] as number)) : undefined;
    if (typeof v === "string") return v;
  } catch { /* not JSON — a plain reason string */ }
  return t;
}

function extractQuery(args: string): string | null {
  try { const o = JSON.parse(args) as { query?: unknown }; return typeof o.query === "string" ? o.query : null; }
  catch { return null; }
}
// Citations look like "[1] Title\n[2] Title". Pull the title after each [n].
function extractSources(result: string): string[] {
  const out: string[] = [];
  const re = /\[\d+\]\s*([^\n[]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(result)) !== null) { const t = m[1].trim(); if (t) out.push(t); }
  return out.slice(0, 8);
}
const num = (v: unknown): number => Number(v) || 0;

export function parseEnrichment(spans: RawSpan[]): Map<string, RoundEnrichment> {
  const byResp = new Map<string, RoundEnrichment>();
  for (const s of spans) {
    const e = byResp.get(s.resp) ?? { inputTokens: 0, outputTokens: 0, cachedTokens: 0, tools: [] };
    if (s.op === "chat") {
      e.inputTokens += num(s.inTok); e.outputTokens += num(s.outTok); e.cachedTokens += num(s.cachedTok);
      if (s.finish) e.finishReason = firstFinish(s.finish);
    } else if (s.op === "execute_tool") {
      const name = s.toolName || "tool";
      let tool = e.tools.find((t) => t.name === name);
      if (!tool) { tool = { name, type: s.toolType || "", count: 0, queries: [], sources: [] }; e.tools.push(tool); }
      tool.count += 1;
      const q = extractQuery(s.toolArgs); if (q) tool.queries.push(q);
      for (const src of extractSources(s.toolResult)) {
        if (tool.sources.length < 8) { if (!tool.sources.includes(src)) tool.sources.push(src); }
      }
    }
    byResp.set(s.resp, e);
  }
  return byResp;
}

export function mergeEnrichment(
  convs: LedgerConversation[], byResp: Map<string, RoundEnrichment>,
): { conversations: LedgerConversation[]; tracesPresent: boolean } {
  let tracesPresent = false;
  const conversations = convs.map((c) => {
    let inTok = 0, outTok = 0, toolCalls = 0, matched = false;
    const detail = c.detail.map((r) => {
      const e = r.responseId ? byResp.get(r.responseId) : undefined;
      if (!e) return r;
      matched = true; tracesPresent = true;
      inTok += e.inputTokens; outTok += e.outputTokens;
      toolCalls += e.tools.reduce((s, t) => s + t.count, 0);
      return { ...r, inputTokens: e.inputTokens, outputTokens: e.outputTokens, cachedTokens: e.cachedTokens, tools: e.tools, finishReason: e.finishReason };
    });
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `matched` is a closure flag mutated by the .map() callback above; TypeScript's CFA doesn't track cross-callback mutations
    if (!matched) return c;
    return { ...c, detail, enriched: true, inputTokens: inTok, outputTokens: outTok, totalTokens: inTok + outTok, toolCalls };
  });
  return { conversations, tracesPresent };
}

interface LogsTableLike { columnDescriptors: { name: string }[]; rows: unknown[][] }
function tableToSpans(t: LogsTableLike): RawSpan[] {
  const idx = Object.fromEntries(t.columnDescriptors.map((c, i) => [c.name, i]));
  const s = (r: unknown[], k: string): string => { const v = r[idx[k]]; if (v == null) return ""; if (typeof v === "string") return v; if (typeof v === "number" || typeof v === "boolean") return String(v); return ""; };
  return t.rows.map((r) => ({
    resp: s(r, "resp"), conv: s(r, "conv"), op: s(r, "op"),
    inTok: num(r[idx.inTok]), outTok: num(r[idx.outTok]), cachedTok: num(r[idx.cachedTok]),
    finish: s(r, "finish"), toolName: s(r, "toolName"), toolType: s(r, "toolType"),
    toolArgs: s(r, "toolArgs"), toolResult: s(r, "toolResult"),
  }));
}

export async function fetchEnrichment(
  credential: TokenCredential, workspaceId: string, respIds: string[], from: string, to: string,
): Promise<Map<string, RoundEnrichment>> {
  // Bound the in() list. respIds arrive newest-first (conversation sort order),
  // so if a window has >250 trace-bearing rounds the OLDEST beyond 250 render as
  // ledger-only — acceptable at MVP volume; revisit with a lazy per-row enrich.
  const ids = respIds.filter((id) => SAFE_ID.test(id)).slice(0, 250);
  if (ids.length === 0) return new Map();
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- LogsQueryClient is deprecated in favour of @azure/monitor-query-logs; migration deferred (separate dependency change)
  const client = new LogsQueryClient(credential);
  const result = await client.queryWorkspace(workspaceId, buildEnrichKql(ids), { startTime: new Date(from), endTime: new Date(to) });
  if (result.status !== LogsQueryResultStatus.Success || result.tables.length === 0) return new Map();
  return parseEnrichment(tableToSpans(result.tables[0] as unknown as LogsTableLike));
}
